import { useState } from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Button,
  Typography,
  Stack,
  Divider,
  Badge,
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
// components
import Avatar from '../../components/Avatar';
import Iconify from '../../components/Iconify';
import SvgIconStyle from '../../components/SvgIconStyle';
import DIOText from '../../components/DIOText';
import {
  InstantMatchingDialog,
} from './';
// hooks
import useAuth from '../../hooks/useAuth';
import useResponsive from '../../hooks/useResponsive';
import { EVENT_ID } from '../../config';

const SecondaryText = styled('span')(({ theme }) => {
  return {
    color: theme.palette.secondary.main,
  };
});

const ErrorText = styled('span')(({ theme }) => {
  return {
    color: theme.palette.error.main,
  };
});

const PrimaryText = styled('span')(({ theme }) => {
  return {
    color: theme.palette.text.primary,
  };
});

// ----------------------------------------------------------------------

export default function InstantMatchingProfile({ application, profile, career, scrollToCareer, mutateInstantProfile, handleAfterSubmit }) {
  const { user } = useAuth();
  const smUp = useResponsive('up', 'sm');
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const hasCareer = career?.length > 0;

  const getCareerName = () => {
    if (career?.length === 0) return "-";

    const current = career?.find((job) => job.end_date === null);
    if (current) {
      return `${current?.is_hidden_career_period ? "" : "현 "}${current?.name}`      
    } else {
      const last = career?.sort((a, b) => a.end_date < b.end_date ? 1 : -1)?.[0];
      return `${last?.is_hidden_career_period ? "" : "전 "}${last?.name}`
    }
  }

  const scrollOrNone = () => {
    if (!hasCareer) {
      scrollToCareer()
    }
  }

  return (
    <Card
      sx={{
        border: '1px solid',
        borderColor: hasCareer ? 'secondary.main' : 'error.main',
      }}
      onClick={scrollOrNone}
    >
      <CardHeader
        title={
          hasCareer ? (
          <Stack direction="row" spacing={smUp ? 1 : 0.5}>
            <CircleLightning />
            <Typography variant="h5"><SecondaryText>즉시 매칭</SecondaryText> 프로필</Typography>
          </Stack>
          ) : (
            <Stack direction="row" spacing={1} alignItems={"center"}>
              <Iconify icon="eva:alert-triangle-fill" sx={{ color: "error.main", width: 24, height: 24 }} />
              <Typography variant="h5"><ErrorText>즉시 매칭 프로필</ErrorText> 미완성</Typography>
            </Stack>
          )
        }
        action={
          hasCareer && (
          <Button
            id={EVENT_ID.crewInstantMatchingProfileShow}
            aria-label="preview"
            variant="outlined"
            size="small"
            sx={{ borderRadius: 2, px: 1.5, py: 0.5 }}
            endIcon={
              <Iconify sx={{ mx: 0 }} icon="ic:baseline-chevron-right" />
            }
            disableRipple
            onClick={() => setOpenEditDialog(true)}
          >
            상세보기
          </Button>
          )
        }
      />
      <CardContent sx={{ pt: 2 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          divider={<Divider orientation={smUp ? "vertical" : "horizontal"} flexItem />}
          spacing={smUp ? 4 : 1.5}
          alignItems={"center"}
        >
          <Stack
            direction={{ xs: "row-reverse", sm: "row" }} spacing={3}
            sx={{
              width: 1,
              opacity: hasCareer ? 1 : 0.5,
            }}
            justifyContent={{ xs: "space-between", sm: "flex-start" }}
          >
            <Badge
              sx={{
                width: 48,
                height: 48,
              }}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              badgeContent={
                <SvgIconStyle src={`/icons/ic_verified.svg`} sx={{ color: "info.main", mr: 2, mb: 3, width: 20, height: 20 }} />
              }
            >
              <Avatar
                src={`https://spacey-dio-public.s3.ap-northeast-2.amazonaws.com/crewji/crewji_${user?.crew?.randomhash || '5'}.png`}
                alt={user?.crew?.nickname}
                color={'default'}
                sx={{
                  margin: 'auto',
                  borderWidth: 2,
                  borderStyle: 'solid',
                  borderColor: 'common.white',
                  width: 48,
                  height: 48,
                }}
              />
            </Badge>
            <Stack spacing={1}>
              <Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="h6">
                    {application?.main_role?.short_name || ''}
                  </Typography>
                  <Typography variant="subtitle1" color="text.secondary">
                    {application?.get_position_display}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center" divider={<Divider orientation={"vertical"} flexItem />}>
                  <Typography variant="caption" color="text.secondary">
                    <PrimaryText>{user?.crew?.nickname}</PrimaryText> 크루
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {application?.years_of_experience}년차
                  </Typography>
                </Stack>
              </Stack>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Typography variant="h6" color={hasCareer ? "secondary.main" : "text.primary"}>
                  {profile?.salary}만원
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  / 주 {profile?.working_hours}시간 근무
                </Typography>
              </Stack>
            </Stack>
          </Stack>
          <Stack sx={{ width: 1 }} spacing={smUp ? 1 : 0.5}>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <SvgIconStyle src={`/icons/ic-job.svg`} sx={{ color: hasCareer ? "text.disabled" : "error.main", width: 16, height: 16 }} />
              {hasCareer ? (
              <Typography variant={smUp ? "body2" : "caption"} color="text.secondary">
                {getCareerName()}
              </Typography>
              ) : (
                <Stack>
                  <Typography variant={smUp ? "subtitle2" : "caption"} color="error.main">
                    <b>경력 사항을 입력해주세요.</b>
                  </Typography>
                  <Typography variant="caption" color="error.main">
                    경력 사항 미입력시 즉시 매칭을 받을 수 없습니다.
                  </Typography>
                </Stack>
              )}
            </Stack>
            {application?.contract_count > 0 && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ opacity: hasCareer ? 1 : 0.5 }}>
              <SvgIconStyle src={`/icons/icon-lightning-mono.svg`} sx={{ color: "text.disabled", width: 16, height: 16 }} />
              <Typography variant={smUp ? "body2" : "caption"} color="text.secondary">
                <DIOText />에서 계약 {application?.contract_count}번째!
              </Typography>
            </Stack>
            )}
          </Stack>
        </Stack>
      </CardContent>
      <InstantMatchingDialog
        application={application}
        profile={profile}
        isOpen={openEditDialog}
        closeDialog={() => setOpenEditDialog(false)}
        mutateInstantProfile={mutateInstantProfile}
        handleAfterSubmit={handleAfterSubmit}
      />
    </Card>
  )
}

function CircleLightning () {
  return (
    <Box
      sx={{
        width: 28,
        height: 28,
        borderRadius: 2,
        backgroundColor: (theme) => alpha(theme.palette.secondary.main, 0.08),
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <SvgIconStyle src={`/icons/icon-lightning-mono.svg`} sx={{ width: 16, height: 16, color: "secondary.main" }} />
    </Box>
  )
}